import { Heart, Target, Lightbulb, Award } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { StatCounter } from "@/components/StatCounter";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";

const values = [
  {
    icon: Heart,
    title: "Integrity",
    text: "We tell it straight — timelines, tradeoffs, and technical realities.",
  },
  {
    icon: Target,
    title: "Customer-Centricity",
    text: "Every decision starts and ends with our customer's outcome.",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    text: "We stay curious, pragmatic, and always looking for a better way.",
  },
  {
    icon: Award,
    title: "Excellence",
    text: "Craft matters. We build things we're proud to put our name on.",
  },
];

const team = [
  {
    name: "Ravi Kulkarni",
    title: "Founder & CEO",
    bio: "20+ years leading engineering across enterprise software and cloud transformations.",
  },
  {
    name: "Neha Iyer",
    title: "Chief Delivery Officer",
    bio: "Runs delivery excellence and program management across our global engagements.",
  },
  {
    name: "Marcus Beltran",
    title: "Head of Cloud Engineering",
    bio: "Cloud architect turned practice leader with deep AWS, Azure, and DevOps expertise.",
  },
  {
    name: "Ananya Rao",
    title: "Head of People",
    bio: "Builds the culture, hiring bar, and career paths that keep our engineers thriving.",
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About — Stantive Soft Solutions"
        description="Learn about Stantive Soft Solutions — a team of technologists building software with simplicity, clarity, and reliability."
        path="/about"
      />
      <PageHero
        eyebrow="About us"
        image="/images/hero-about.jpg"
        imagePosition="center"
        title="A senior technology team built around clarity and craft."
        description="We're IT professionals passionate about technology and committed to delivering with simplicity, clarity, and reliability, even on the most complex engagements."
        stats={[
          { value: "12+", label: "Years" },
          { value: "180+", label: "Projects" },
          { value: "65+", label: "Clients" },
          { value: "220+", label: "Team" },
        ]}
      />

      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:items-start lg:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-bold">Who we are</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Stantive brings together experienced engineers, architects, and consultants who love
              the craft of software. We work with startups, growth-stage companies, and enterprises
              to solve their hardest technical problems and turn ideas into products customers love.
            </p>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Our approach favors clarity over complexity, iteration over ceremony, and long-term
              partnership over transactional delivery.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-display text-3xl font-bold">Our story</h2>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              Stantive was founded to bridge the widening gap between rapidly evolving technology
              and the real business outcomes companies need. Too often, teams inherit heavy
              processes, opaque roadmaps, and mediocre code.
            </p>
            <p className="mt-4 leading-relaxed text-muted-foreground">
              We set out to do it differently — small senior teams, transparent communication, and a
              relentless focus on shipping software that works in production and moves the business
              forward.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary/40 py-20">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="h-full rounded-2xl border border-border bg-card p-10 shadow-card">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                Mission
              </span>
              <h3 className="mt-2 font-display text-2xl font-bold">
                Help every business we work with ship better software, faster.
              </h3>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                We bring the discipline, talent, and technical depth needed to turn ambitious ideas
                into reliable, production-grade products.
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="h-full rounded-2xl border border-border bg-card p-10 shadow-card">
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                Vision
              </span>
              <h3 className="mt-2 font-display text-2xl font-bold">
                Be the most trusted software partner for businesses that take technology seriously.
              </h3>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                A partner known for craft, character, and consistent outcomes — across every
                geography we serve.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Core values
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">What we stand for</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 80}>
                <div className="h-full rounded-2xl border border-border bg-card p-6 text-center shadow-card">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
                    <v.icon size={22} />
                  </div>
                  <h3 className="mt-4 font-semibold">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Leadership
            </span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
              Meet the team leading Stantive
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {team.map((m, i) => (
              <Reveal key={m.name} delay={i * 80}>
                <div className="h-full rounded-2xl border border-border bg-card p-6 shadow-card">
                  <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-gradient-hero text-2xl font-bold text-primary-foreground shadow-elegant">
                    {m.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <h3 className="mt-4 text-center font-semibold">{m.name}</h3>
                  <p className="text-center text-xs font-medium text-primary">{m.title}</p>
                  <p className="mt-3 text-center text-sm leading-relaxed text-muted-foreground">
                    {m.bio}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 rounded-3xl bg-gradient-ink p-10 shadow-elegant md:grid-cols-4">
            <StatCounter value={12} label="Years of Experience" suffix="+" dark />
            <StatCounter value={180} label="Projects Delivered" suffix="+" dark />
            <StatCounter value={65} label="Clients Served" suffix="+" dark />
            <StatCounter value={220} label="Team Members" suffix="+" dark />
          </div>
        </div>
      </section>
    </>
  );
}
